import type { ReactElement } from "react";

import { cn } from "~/lib/utils";
import { Tooltip, TooltipPopup, TooltipTrigger } from "./ui/tooltip";
import { formatRailHoverTime, type RailHoverFacts } from "./railHoverCard.logic";

/**
 * The Room Rail's hover card (RM-CAN-10, dated revision 2026-09-15). A rail
 * row is a door with a ceiling on its width; the card says the rest beside
 * the rail, where there is room: the full name, the room, the standing, when
 * it was born, when the last message moved, and what the person last said.
 *
 * It rides the one Tooltip primitive so it inherits the rail's intent delay,
 * the grace period between rows, keyboard focus, Escape, and the portal —
 * and it is pointer-events-none by that primitive's law, so it never stands
 * between the pointer and the row's own doors (⋮, retire, pin, the chevron).
 * The trigger element keeps its aria-label; the card is description, not name.
 */
export function RailHoverCard({
  facts,
  disabled = false,
  trigger,
  className,
}: {
  readonly facts: RailHoverFacts;
  /** Dragging, renaming, or an open menu on the row silences the card. */
  readonly disabled?: boolean;
  readonly trigger: ReactElement;
  readonly className?: string;
}): ReactElement {
  return (
    <Tooltip disabled={disabled}>
      <TooltipTrigger render={trigger} />
      <TooltipPopup
        align="start"
        className={cn("w-72 max-w-[min(18rem,calc(100vw-24px))] text-left", className)}
        data-rail-hover-card={facts.kind}
        role="tooltip"
        side="right"
        sideOffset={14}
        variant="glass"
      >
        <RailHoverCardBody facts={facts} />
      </TooltipPopup>
    </Tooltip>
  );
}

const KIND_WORD: Record<RailHoverFacts["kind"], string> = {
  room: "Room",
  session: "Session",
  workroom: "Workroom",
};

export function RailHoverCardBody({
  facts,
  nowMs = Date.now(),
}: {
  readonly facts: RailHoverFacts;
  readonly nowMs?: number;
}): ReactElement {
  const created = formatRailHoverTime(facts.createdAt, nowMs);
  const last = formatRailHoverTime(facts.lastMessageAt, nowMs);
  return (
    <div className="flex w-full min-w-0 flex-col gap-1.5 p-1.5" data-rail-hover-card-body="">
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="text-[9px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
          {KIND_WORD[facts.kind]}
        </span>
        <p
          className="line-clamp-3 break-words text-xs font-medium leading-snug text-foreground"
          data-rail-hover-title=""
        >
          {facts.title}
        </p>
        {facts.meta ? (
          <p className="text-[10px] leading-snug text-muted-foreground" data-rail-hover-meta="">
            {facts.meta}
          </p>
        ) : null}
      </div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[10px] leading-snug">
        <dt className="text-muted-foreground">Created</dt>
        <dd className="min-w-0 truncate text-foreground/90" data-rail-hover-created="">
          {created ? <time dateTime={created.dateTime}>{created.absolute}</time> : "—"}
        </dd>
        <dt className="text-muted-foreground">Last message</dt>
        <dd className="min-w-0 truncate text-foreground/90" data-rail-hover-last="">
          {last ? (
            <>
              <time dateTime={last.dateTime}>{last.absolute}</time>
              <span className="text-muted-foreground"> · {last.relative}</span>
            </>
          ) : (
            "—"
          )}
        </dd>
      </dl>
      {facts.lastUserMessage ? (
        <p
          className="line-clamp-3 border-l-2 border-border pl-2 text-[11px] leading-snug text-muted-foreground"
          data-rail-hover-preview=""
        >
          <span className="font-medium text-foreground/80">You</span> {facts.lastUserMessage}
        </p>
      ) : null}
      {facts.hint ? (
        <p className="text-[9px] leading-snug text-muted-foreground/70" data-rail-hover-hint="">
          {facts.hint}
        </p>
      ) : null}
    </div>
  );
}
